// @/services/app/websocket/index.js

import { logger } from '@/utils/logger';
import { getStrapiToken } from '@/services/auth/getStrapiToken';

const log = logger.websocket;

/**
 * WebSocketHandler
 *
 * Manages real-time WebSocket communication with sophisticated features:
 * - Module-specific event dispatching with intelligent routing
 * - Carbon Accounting optimized event handling to prevent cascade refreshes
 * - Resilient reconnection logic with exponential backoff
 * - Event deduplication to prevent duplicate processing
 * - Keep-alive mechanism to prevent Heroku H15 idle timeouts
 * - Optimized sequential data loading for Carbon Accounting modules
 * - Proper immutable state updates for React/Gatsby components
 * - Browser online/offline detection and connection management
 */
export class WebSocketHandler {
  /**
   * Initialize the WebSocket handler
   * @param {Object} store - The main application store
   */
  constructor(store) {
    this.store = store;
    this.connected = false;
    this.socket = null;

    // Event deduplication
    this.processedEvents = new Map();
    this.processedEventsMaxSize = 100;
    this.eventTTL = 30 * 1000;

    // Event handlers registry
    this.handlers = new Map();

    // Reconnection settings
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimer = null;
    this.reconnectDelay = 2000;

    // Keep-alive for Heroku (prevents H15 errors)
    this.pingTimer = null;
    this.pingInterval = 20000; // 20 seconds

    // Track last known Carbon Accounting calculation status
    this.lastCalculationStatus = {
      timestamp: null,
      projectId: null,
      inProgress: false
    };

    if (typeof window !== 'undefined') {
      log.log('WebSocketHandler initialized');

      // Listen for online/offline events
      window.addEventListener('online', () => this.setupSocket());
      window.addEventListener('offline', () => this.cleanupConnection());
    }
  }

  /**
   * Initialize the WebSocket connection
   * Sets up socket, registers handlers, and starts event cleanup
   * 
   * @returns {Promise<boolean>} Success status of initialization
   */
  async initialize() {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      log.log('Starting WebSocket connection');
      this.setupSocket();
      this.registerDefaultHandlers();
      this.startEventCleanup();
      return true;
    } catch (error) {
      log.error('WebSocket initialization failed:', error);
      return false;
    }
  }

  /**
   * Register a handler for specific event types
   * 
   * @param {string} handlerId - Unique identifier for the handler
   * @param {Function} handlerFn - Function to handle the events
   * @param {Array<string>} eventTypes - Array of event types to handle, defaults to ['*']
   * @param {Object} context - Context to bind the handler function to
   * @returns {boolean} Success status of registration
   */
  registerHandler(handlerId, handlerFn, eventTypes = [], context = null) {
    if (typeof handlerFn !== 'function') {
      log.warn(`Invalid handler function for ${handlerId}`);
      return false;
    }

    // Default to all events if none specified
    if (!eventTypes || eventTypes.length === 0) {
      eventTypes = ['*'];
    }

    // Wrap handler function to ensure proper context
    const wrappedHandler = context ? handlerFn.bind(context) : handlerFn;
    wrappedHandler.handlerId = handlerId;

    // Register for each event type
    for (const type of eventTypes) {
      if (!this.handlers.has(type)) {
        this.handlers.set(type, []);
      }
      this.handlers.get(type).push(wrappedHandler);
    }

    return true;
  }

  /**
   * Unregister a handler by ID
   * 
   * @param {string} handlerId - ID of the handler to unregister
   * @returns {boolean} True if handler was found and removed
   */
  unregisterHandler(handlerId) {
    let found = false;

    this.handlers.forEach((handlers, _type) => {
      const handlerIndex = handlers.findIndex(handler =>
        handler.handlerId === handlerId
      );

      if (handlerIndex !== -1) {
        handlers.splice(handlerIndex, 1);
        found = true;
      }
    });

    return found;
  }

  /**
   * Set up the WebSocket connection
   * Establishes connection with the server and sets up event handlers
   */
  setupSocket() {
    // Clean up existing connection first
    this.cleanupConnection();

    const projectId = this.store.getProjectId();
    if (!projectId) {
      log.warn('No project ID available for WebSocket connection');
      return;
    }

    // The server authenticates the handshake: the user's Keycloak JWT rides as
    // a Sec-WebSocket-Protocol entry next to the `kc-bearer` marker (browsers
    // cannot set headers on a WebSocket, and the URL would leak the token into
    // access logs). Without a session there is nothing to authenticate with —
    // retry via the normal reconnect loop until auth init provides a token.
    const token = getStrapiToken();
    if (!token) {
      log.warn('No session token available for WebSocket connection');
      this.scheduleReconnect();
      return;
    }

    const strapiUrl = process.env.GATSBY_STRAPI_URL || '';
    const wsProtocol = strapiUrl.startsWith('https') ? 'wss://' : 'ws://';
    const host = strapiUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}${host}/ws?projectId=${projectId}`;

    log.log(`Connecting to WebSocket: ${wsUrl}`);

    try {
      this.socket = new WebSocket(wsUrl, ['kc-bearer', token]);

      this.socket.onopen = () => {
        log.log('WebSocket connection established');
        this.connected = true;
        this.reconnectAttempts = 0;

        // Start ping to keep the connection alive
        this.startPingTimer();

        // Refresh data on connection
        this.refreshDataOnConnection();
      };

      this.socket.onclose = (event) => {
        this.connected = false;
        log.log(`WebSocket connection closed (Code: ${event.code})`);

        this.cleanupTimers();

        // Attempt to reconnect unless browser is offline
        if (navigator.onLine !== false) {
          this.scheduleReconnect();
        }
      };

      this.socket.onerror = (error) => {
        log.error('WebSocket connection error:', error);
        this.connected = false;
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle pong responses from server
          if (data.type === 'pong') {
            return; // Don't process pongs as regular events
          }

          this.handleEvent(data);
        } catch (error) {
          log.error('Error processing WebSocket message:', error);
        }
      };
    } catch (error) {
      log.error('Error creating WebSocket connection:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Clean up WebSocket connection
   * Closes socket and cleans up resources
   */
  cleanupConnection() {
    this.cleanupTimers();

    if (this.socket) {
      const socket = this.socket;
      this.socket = null;

      try {
        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
          socket.close(1000, 'Client disconnecting');
        }
      } catch (e) {
        log.error('Error closing socket:', e);
      }
    }

    this.connected = false;
  }

  /**
   * Clean up all timers
   * @private
   */
  cleanupTimers() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this._reminderDebounceTimer) {
      clearTimeout(this._reminderDebounceTimer);
      this._reminderDebounceTimer = null;
    }
  }

  /**
   * Schedule a reconnection attempt
   * Uses exponential backoff with jitter for reconnection timing
   * @private
   */
  scheduleReconnect() {
    if (this.reconnectTimer) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      log.log('Maximum reconnection attempts reached. Giving up.');
      return;
    }

    const delay = Math.min(
      30000, // Max 30 seconds
      this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts) * (0.9 + Math.random() * 0.2)
    );

    log.log(`Scheduling WebSocket reconnection in ${Math.round(delay / 1000)}s (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnectAttempts++;

      log.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
      this.setupSocket();
    }, delay);
  }

  /**
   * Start the ping timer to prevent idle connection timeouts (H15) on Heroku
   * @private
   */
  startPingTimer() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
    }

    this.pingTimer = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        try {
          // Send ping message to keep connection alive
          this.socket.send(JSON.stringify({
            type: 'ping',
            timestamp: Date.now()
          }));
        } catch (error) {
          log.error('Error sending ping:', error);
          this.cleanupConnection();
          this.scheduleReconnect();
        }
      }
    }, this.pingInterval);
  }

  /**
   * Start periodic cleanup of processed events
   * Prevents memory leaks by pruning old events
   * @private
   */
  startEventCleanup() {
    setInterval(() => {
      const now = Date.now();
      const expiredTime = now - this.eventTTL;

      // Remove expired events
      for (const [key, timestamp] of this.processedEvents) {
        if (timestamp < expiredTime) {
          this.processedEvents.delete(key);
        }
      }

      // If map is still too large, remove oldest entries
      if (this.processedEvents.size > this.processedEventsMaxSize) {
        const entriesToRemove = this.processedEvents.size - this.processedEventsMaxSize;
        const entries = Array.from(this.processedEvents.entries());

        entries.sort((a, b) => a[1] - b[1]);

        for (let i = 0; i < entriesToRemove; i++) {
          this.processedEvents.delete(entries[i][0]);
        }
      }
    }, 30000);
  }

  /**
   * Generate a unique ID for an event
   * Used to prevent duplicate event processing
   * 
   * @private
   * @param {Object} event - The event to generate ID for
   * @returns {string} Unique event identifier
   */
  getEventId(event) {
    const { type, action, projectId, linkingId, id, timestamp } = event;

    if (type === 'submission:complete' || type === 'submission:status') {
      return `${type}:${projectId}:${timestamp}`;
    }

    if (type === 'customer-request:update' || type === 'supplier-input' || type === 'customer-request:new-invitation') {
      return `${type}:${linkingId || id}:${action}:${timestamp}`;
    }

    // ALWAYS include timestamp — different events for the same entity must not collide.
    // Previous code: `${id || linkingId || timestamp}` — the || chain meant timestamp
    // was NEVER included when id/linkingId existed, causing accept→decline for the
    // same supplier to generate identical dedup keys and the 2nd event to be swallowed.
    return `${type}:${action}:${id || linkingId}:${timestamp}`;
  }

  /**
   * Handle incoming WebSocket event
   * Processes event, checks for duplicates, and dispatches to appropriate handlers
   * 
   * @param {Object} event - The WebSocket event to handle
   */
  handleEvent(event) {
    const normalizedEvent = this.normalizeEvent(event);

    if (!normalizedEvent.type) {
      log.warn('Received event without type:', event);
      return;
    }

    const eventId = this.getEventId(normalizedEvent);

    if (this.processedEvents.has(eventId)) {
      log.log(`Skipping duplicate event: ${normalizedEvent.type}${normalizedEvent.action ? ':' + normalizedEvent.action : ''}`);
      return;
    }

    this.processedEvents.set(eventId, Date.now());
    
    log.log(`WebSocket event: ${normalizedEvent.type}${normalizedEvent.action ? ':' + normalizedEvent.action : ''}`);

    // OPTIMIZATION: Handle Carbon Accounting events specifically
    if (this.isCarbonAccountingEvent(normalizedEvent)) {
      this.handleCarbonAccountingEvent(normalizedEvent);
    } else {
      // Process non-Carbon Accounting events normally
      this.dispatchEventToHandlers(normalizedEvent);
      this.processSpecialEvents(normalizedEvent);
    }
  }

  /**
   * Check if an event is related to Carbon Accounting
   * 
   * @private
   * @param {Object} event - The event to check
   * @returns {boolean} True if this is a Carbon Accounting event
   */
  isCarbonAccountingEvent(event) {
    // Check if event type starts with any Carbon Accounting prefix
    const carbonPrefixes = [
      'ca-emission-results',
      'ca-activities-data',
      'ca-spend-volume',
      'ca-spend-distribution',
      'ca-supplier-input',
      'carbonaccounting',
      'emission-result'  // ✅ Backend sends 'emission-result:update' (singular)
    ];

    // Check if event has a Carbon Accounting-specific type
    if (event.type && carbonPrefixes.some(prefix => event.type.startsWith(prefix))) {
      return true;
    }

    // Also check for calculation events
    if (event.type === 'calculation' || 
        (event.type === 'notification' && event.data?.type === 'calculation')) {
      return true;
    }

    return false;
  }

  /**
   * Handle Carbon Accounting specific events
   * Prevents cascade refreshes by intelligently refreshing only affected modules
   * 
   * @private
   * @param {Object} event - The WebSocket event
   */
  handleCarbonAccountingEvent(event) {
    log.log('Processing Carbon Accounting event:', event.type);

    // Map event types to module names and their dependencies
    const caModuleMap = {
      'ca-emission-results': {
        moduleName: 'emissionResults',
        handlerMethod: 'emissionsHandler.loadAllEmissionResults',
        // Emission results don't affect other modules
        refreshDependencies: false
      },
      'ca-activities-data': {
        moduleName: 'activityData',
        handlerMethod: 'activitiesHandler.loadActivityData',
        // Activity data affects emission results
        refreshDependencies: true,
        triggerCalculation: true
      },
      'ca-spend-volume': {
        moduleName: 'spendVolume',
        handlerMethod: 'spendHandler.loadSpendVolume',
        // Spend volume affects emission results
        refreshDependencies: true,
        triggerCalculation: true
      },
      'ca-spend-distribution': {
        moduleName: 'spendDistribution',
        handlerMethod: 'spendDistributionHandler.loadSpendDistribution',
        // Spend distribution affects supplier inputs and emission results
        refreshDependencies: true,
        dependentModules: ['supplierInput'],
        triggerCalculation: true
      },
      'ca-supplier-input': {
        moduleName: 'supplierInput',
        handlerMethod: 'supplierInputHandler.loadSupplierInput',
        // Supplier input affects emission results
        refreshDependencies: true,
        triggerCalculation: true
      }
    };

    // Handle calculation status events
    if (event.type === 'calculation' ||
        (event.type === 'notification' && event.data?.type === 'calculation')) {
      this.handleCalculationStatusEvent(event);
      return;
    }

    // Handle emission-result events that affect suppliers
    // These are sent by backend after CA calculation or year deletion
    // Backend syncs supplychain-suppliers to Strapi before sending this event
    if (event.type === 'emission-result:update' && event.action === 'calculate') {
      log.log('[WebSocket] emission-result:update (calculate) received:', {
        linkingIds: event.linkingIds,
        linkingIdsCount: event.linkingIds?.length || 0
      });

      // 1. Reload emission results
      if (this.store.emissionsHandler) {
        this.store.emissionsHandler.loadAllEmissionResults(true).catch(err => {
          log.warn('Error reloading emission results after calculation:', err);
        });
      }

      // 2. Update suppliers - use targeted update if linkingIds provided, otherwise full reload
      if (this.store.supplychainSuppliersHandler) {
        if (event.linkingIds && Array.isArray(event.linkingIds) && event.linkingIds.length > 0) {
          log.log('[WebSocket] Triggering targeted supplier update for linkingIds:', event.linkingIds);
          // Targeted update - only fetch the suppliers that were assigned emissions
          this.store.supplychainSuppliersHandler.fetchAndUpdateByLinkingIds(event.linkingIds)
            .then(success => {
              log.log('[WebSocket] Targeted supplier update result:', success);
              if (!success) {
                // Fall back to full reload if targeted update fails
                return this.store.supplychainSuppliersHandler.loadSupplychainSuppliers(true);
              }
            })
            .catch(err => {
              log.warn('Targeted supplier update failed, falling back to full reload:', err);
              this.store.supplychainSuppliersHandler.loadSupplychainSuppliers(true);
            });
        } else {
          log.log('[WebSocket] No linkingIds in event, falling back to full reload');
          // No linkingIds - fall back to full reload
          this.store.supplychainSuppliersHandler.markStale(
            'CA calculation completed (emission-result:update)',
            true
          );
          this.store.supplychainSuppliersHandler.loadSupplychainSuppliers(true).catch(err => {
            log.warn('Error reloading suppliers after calculation:', err);
          });
        }
      }

      // Also dispatch to registered handlers
      this.dispatchEventToHandlers(event);
      return;
    }

    // ✅ Handle emission-result:delete - year deletion also affects suppliers
    if (event.type === 'emission-result:delete') {
      log.log('[WebSocket] CA year deleted - handling emission-result:delete');

      // 1. Reload emission results
      if (this.store.emissionsHandler) {
        this.store.emissionsHandler.loadAllEmissionResults(true).catch(err => {
          log.warn('Error reloading emission results after year deletion:', err);
        });
      }

      // 2. Reload suppliers immediately - year deletion affects invitedSupplyChain count
      if (this.store.supplychainSuppliersHandler) {
        this.store.supplychainSuppliersHandler.markStale(
          'CA year deleted (emission-result:delete)',
          true
        );
        this.store.supplychainSuppliersHandler.loadSupplychainSuppliers(true).catch(err => {
          log.warn('Error reloading suppliers after year deletion:', err);
        });
      }

      // Also dispatch to registered handlers
      this.dispatchEventToHandlers(event);
      return;
    }

    // Find which module this event is for
    let targetModule = null;
    for (const [prefix, moduleInfo] of Object.entries(caModuleMap)) {
      if (event.type.startsWith(prefix)) {
        targetModule = moduleInfo;
        break;
      }
    }

    if (!targetModule) {
      log.log(`Unknown Carbon Accounting event type: ${event.type}`);
      // Dispatch to all handlers as fallback
      this.dispatchEventToHandlers(event);
      return;
    }

    // First, reload the specific module that changed
    this.reloadCarbonAccountingModule(targetModule.moduleName, targetModule.handlerMethod);

    // If this module affects other modules, reload them as well
    if (targetModule.refreshDependencies) {
      if (targetModule.dependentModules) {
        // Reload specific dependent modules
        targetModule.dependentModules.forEach(moduleName => {
          const depModule = Object.values(caModuleMap).find(m => m.moduleName === moduleName);
          if (depModule) {
            this.reloadCarbonAccountingModule(depModule.moduleName, depModule.handlerMethod);
          }
        });
      }

      // Trigger calculation if needed
      if (targetModule.triggerCalculation) {
        const projectId = this.store.getProjectId();
        const year = this.extractYearFromEvent(event);

        if (projectId && year && this.store.emissionsHandler) {
          // Trigger recalculation for the affected year
          this.store.emissionsHandler.flagYearForRecalculation(year).catch(err => {
            log.warn('Error flagging year for recalculation:', err);
          });
        }
      }
    }

    // Finally, dispatch to registered handlers
    this.dispatchEventToHandlers(event);
  }

  /**
   * Extract year information from a Carbon Accounting event
   * 
   * @private
   * @param {Object} event - The event to extract year from
   * @returns {string|null} The year or null if not found
   */
  extractYearFromEvent(event) {
    // Try to extract year information from various event fields
    if (event.year) {
      return event.year.toString();
    }

    if (event.data && event.data.year) {
      return event.data.year.toString();
    }

    // Try to extract from payload
    if (event.payload) {
      if (event.payload.year) {
        return event.payload.year.toString();
      }
      if (event.payload.data && event.payload.data.year) {
        return event.payload.data.year.toString();
      }
    }

    // Default to current year if we can't extract
    return new Date().getFullYear().toString();
  }

  /**
   * Handle calculation status events
   * Updates application state and triggers reloads when calculations complete
   * 
   * @private
   * @param {Object} event - The calculation event
   */
  handleCalculationStatusEvent(event) {
    const status = this.extractCalculationStatus(event);

    // Update state with calculation status for UI feedback only
    // NOTE: Actual CA calculation completion is handled via 'emission-result:update' events
    // in handleCarbonAccountingEvent(), which includes targeted supplier updates.
    // This handler is only for generic calculation status tracking.
    if (status) {
      if (this.store.state) {
        this.store.state.calculationStatus = status;
      }

      this.lastCalculationStatus = {
        ...status,
        timestamp: Date.now()
      };
    }

    // Dispatch to registered handlers
    this.dispatchEventToHandlers(event);
  }

  /**
   * Extract calculation status from event
   * 
   * @private
   * @param {Object} event - The calculation event
   * @returns {Object|null} Calculation status object or null
   */
  extractCalculationStatus(event) {
    if (event.type === 'calculation') {
      return {
        status: event.status || event.action || 'unknown',
        projectId: event.projectId || this.store.getProjectId(),
        year: event.year || (event.data && event.data.year),
        timestamp: event.timestamp || new Date().toISOString()
      };
    }
    
    if (event.type === 'notification' && event.data?.type === 'calculation') {
      return {
        status: event.data.status || event.data.action || 'unknown',
        projectId: event.data.projectId || this.store.getProjectId(),
        year: event.data.year,
        timestamp: event.timestamp || new Date().toISOString()
      };
    }
    
    return null;
  }

  /**
   * Reload a specific Carbon Accounting module
   * 
   * @private
   * @param {string} moduleName - The module name to reload
   * @param {string} handlerMethod - The handler method path
   * @returns {Promise<boolean>} Success status
   */
  async reloadCarbonAccountingModule(moduleName, handlerMethod) {
    try {
      if (!this.store) {
        log.warn(`Cannot reload ${moduleName}: store not available`);
        return false;
      }

      if (!handlerMethod) {
        log.warn(`No handler method defined for ${moduleName}`);
        return false;
      }

      const [handlerName, methodName] = handlerMethod.split('.');

      if (this.store[handlerName] && typeof this.store[handlerName][methodName] === 'function') {
        log.log(`WebSocket triggered reload of ${moduleName} via ${handlerMethod}`);
        
        await this.store[handlerName][methodName](true);
        return true;
      } else {
        log.warn(`Handler method ${handlerMethod} not found for ${moduleName}`);
        return false;
      }
    } catch (error) {
      log.error(`Error reloading ${moduleName}:`, error);
      return false;
    }
  }

  /**
   * Normalize event data to consistent format
   * 
   * @private
   * @param {Object} data - Raw event data
   * @returns {Object} Normalized event object
   */
  normalizeEvent(data) {
    const event = { ...data };

    if (!event.timestamp) {
      event.timestamp = new Date().toISOString();
    }

    if (!event.type && event.eventType) {
      event.type = event.eventType;
    }

    if (!event.action) {
      if (event.method) event.action = event.method;
      else if (event.status) event.action = event.status;
    }

    if (!event.projectId && event.data && event.data.projectId) {
      event.projectId = event.data.projectId;
    }

    if (event.data && typeof event.data === 'object') {
      if (!event.action && event.data.action) {
        event.action = event.data.action;
      }
      if (event.data.linkingId && !event.linkingId) {
        event.linkingId = event.data.linkingId;
      }
      // Copy linkingIds array for targeted updates (Operation Lightsaber)
      if (event.data.linkingIds && !event.linkingIds) {
        event.linkingIds = event.data.linkingIds;
      }
    }

    const isReminder =
      event.action === 'remind' ||
      (event.type && event.type.includes('remind'));

    if (isReminder) {
      event.isReminder = true;
    }

    return event;
  }

  /**
   * Dispatch event to registered handlers
   * 
   * @private
   * @param {Object} event - The normalized event to dispatch
   */
  dispatchEventToHandlers(event) {
    const { type, action } = event;

    const typeActionKey = action ? `${type}:${action}` : null;
    if (typeActionKey && this.handlers.has(typeActionKey)) {
      this.executeHandlers(this.handlers.get(typeActionKey), event);
    }

    if (this.handlers.has(type)) {
      this.executeHandlers(this.handlers.get(type), event);
    }

    if (this.handlers.has('*')) {
      this.executeHandlers(this.handlers.get('*'), event);
    }
  }

  /**
   * Execute a list of handlers for an event
   * 
   * @private
   * @param {Array<Function>} handlers - Array of handler functions
   * @param {Object} event - Event to pass to handlers
   */
  executeHandlers(handlers, event) {
    if (!handlers || !Array.isArray(handlers)) return;

    for (const handler of handlers) {
      try {
        handler(event);
      } catch (error) {
        log.error('Error in event handler:', error);
      }
    }
  }

  /**
   * Process special event types that need direct action
   * 
   * @private
   * @param {Object} event - The event to process
   */
  processSpecialEvents(event) {
    if (event.isReminder || event.action === 'remind') {
      this.handleReminderEvent(event);
      return;
    }

    if (event.type === 'submission:complete') {
      this.handleSubmissionEvent(event);
      return;
    }
    // NOTE: customer-request:update with action 'submit' is also emitted by the backend,
    // but submission:complete is the canonical event. No need to handle both —
    // it was causing handleSubmissionEvent to fire twice per submission.

    if (event.action === 'populate' || event.type?.includes('populate')) {
      this.handlePopulateEvent(event);
      return;
    }

    if (event.action === 'invite' || (event.type && event.type.includes('invite'))) {
      this.handleInviteEvent(event);
      return;
    }

    if (event.action?.toLowerCase() === 'accept' || event.action?.toLowerCase() === 'decline') {
      this.handleStatusChangeEvent(event);
      return;
    }

    // ✅ CROSS-PROJECT: Handle new invitation notification (Supplier receives this)
    // Operation Lightsaber Part 2: Real-time notification when Lead Firm invites existing supplier
    if (event.type === 'customer-request:new-invitation') {
      this.handleNewInvitationEvent(event);
      return;
    }

    // ✅ CROSS-PROJECT: Handle reminder received notification (Supplier receives this)
    // Operation Lightsaber: When Lead Firm sends reminder, supplier's customer-requests update
    if (event.type === 'customer-request:reminder-received') {
      this.handleReminderReceivedEvent(event);
      return;
    }

    // ✅ CROSS-PROJECT: Handle supplier data changes from other projects
    // This is triggered when a supplier updates their self-assessment
    // plausibility-check-completed uses the same linkingIds pattern — route it here too
    if (event.type === 'supplychain-supplier:data-changed' ||
        event.type === 'supplychain-supplier:plausibility-check-completed') {
      this.handleSupplierDataChangedEvent(event);
      return;
    }

    // ✅ CROSS-PROJECT: Handle supplier submission notification (Operation Lightsaber)
    // This is triggered when a supplier submits their assessment - Lead Firm should see it instantly
    if (event.type === 'supplychain-supplier:submitted') {
      this.handleSupplierSubmittedEvent(event);
      return;
    }

    // ✅ CROSS-PROJECT: Handle supplier status change (create-project, accept, etc.)
    // This is triggered when a supplier accepts invitation / creates project
    if (event.type === 'supplychain-supplier:status-changed') {
      this.handleSupplierStatusChangedEvent(event);
      return;
    }
  }

  /**
   * Handle supplier data changed events from cross-project notifications
   * Triggered when a supplier in our supply chain makes changes that affect our SCM data.
   * Reasons include:
   * - supplier-ca-calculation-completed: Supplier ran CA calculation
   * - supplier-ca-year-deleted: Supplier deleted a CA year
   * - supplier-ca-supplier-assigned: Supplier assigned an external supplier in CA
   * - supplier-ca-supplier-updated: Supplier updated a supplier assignment
   * - supplier-ca-supplier-removed: Supplier removed a supplier assignment
   * - self-assessment update: Supplier updated their self-assessment answers
   *
   * @private
   * @param {Object} event - The supplier data changed event
   */
  handleSupplierDataChangedEvent(event) {
    const currentProjectId = this.store.getProjectId();

    // Only process if this event is for our project (Lead Firm)
    if (event.leadFirmProjectId && event.leadFirmProjectId !== currentProjectId) {
      log.log(`[WebSocket] Ignoring supplier data change for different project: ${event.leadFirmProjectId}`);
      return;
    }

    log.log('[WebSocket] supplychain-supplier:data-changed received', {
        supplierProjectId: event.supplierProjectId,
        reason: event.reason,
        linkingIdsCount: event.linkingIds?.length || 0
      });

    // Targeted updates: Backend sends linkingIds so we update ONLY the affected suppliers
    if (this.store.supplychainSuppliersHandler) {
      // If linkingIds provided, do targeted update (faster for large supplier lists)
      if (event.linkingIds && Array.isArray(event.linkingIds) && event.linkingIds.length > 0) {
        this.store.supplychainSuppliersHandler.fetchAndUpdateByLinkingIds(event.linkingIds)
          .then(success => {
            if (!success) {
              // Fall back to full reload if targeted update fails
              log.warn('[WebSocket] Targeted update failed, falling back to full reload');
              return this.store.supplychainSuppliersHandler.loadSupplychainSuppliers(true);
            }
          })
          .catch(err => {
            log.warn('Error in targeted supplier update:', err);
            // Fall back to full reload
            this.store.supplychainSuppliersHandler.loadSupplychainSuppliers(true);
          });
      } else {
        // No linkingIds - fall back to full reload
        this.store.supplychainSuppliersHandler.markStale(
          `Supplier ${event.supplierProjectId} data changed: ${event.reason}`,
          true
        );
        this.store.supplychainSuppliersHandler.loadSupplychainSuppliers(true).catch(err => {
          log.warn('Error reloading suppliers after cross-project update:', err);
        });
      }
    }
  }

  /**
   * Handle supplier submitted event (Lead Firm receives this)
   * Operation Lightsaber: Real-time notification when supplier submits their assessment
   * This enables Lead Firms to see submission status instantly instead of 0-5 min delay
   *
   * @private
   * @param {Object} event - The submission event from supplier
   */
  handleSupplierSubmittedEvent(event) {
    const currentProjectId = this.store.getProjectId();

    // Only process if this event is for our project (Lead Firm)
    if (event.leadFirmProjectId &&
        parseInt(event.leadFirmProjectId) !== parseInt(currentProjectId)) {
      return;
    }

    log.log('[WebSocket] Supplier submitted assessment:', {
        supplierProjectId: event.supplierProjectId,
        timestamp: event.timestamp,
        assessmentStatus: event.assessmentStatus
      });

    // Mark suppliers as stale and reload IMMEDIATELY
    // ✅ Operation Lightsaber: Backend already synced data to Strapi before WebSocket
    if (this.store.supplychainSuppliersHandler) {
      this.store.supplychainSuppliersHandler.markStale(
        `Supplier ${event.supplierProjectId} submitted assessment`,
        true // triggerImmediate
      );

      // Trigger immediate reload - no delay needed since Strapi already has fresh data
      log.log('[WebSocket] Supplier submitted - triggering immediate reload');

      this.store.supplychainSuppliersHandler.loadSupplychainSuppliers(false).catch(err => {
        log.warn('[WebSocket] Error reloading suppliers after submission:', err);
      });
    }
  }

  /**
   * Handle supplier status changed event (Lead Firm receives this)
   * Operation Lightsaber: Triggered when supplier creates project / accepts invitation
   * This enables Lead Firms to see the status change from "invited" to "accepted" instantly
   *
   * @private
   * @param {Object} event - The status change event from supplier registration
   */
  handleSupplierStatusChangedEvent(event) {
    const currentProjectId = this.store.getProjectId();

    // Only process if this event is for our project (Lead Firm)
    if (event.leadFirmProjectId &&
        parseInt(event.leadFirmProjectId) !== parseInt(currentProjectId)) {
      return;
    }

    log.log('[WebSocket] Supplier status changed:', {
        supplierProjectId: event.supplierProjectId,
        linkingId: event.linkingId,
        newStatus: event.newStatus,
        reason: event.reason
      });

    // Mark suppliers as stale and reload IMMEDIATELY - Strapi already has fresh data from backend sync
    // ✅ Operation Lightsaber: No delay needed since backend synced before sending WebSocket
    if (this.store.supplychainSuppliersHandler) {
      this.store.supplychainSuppliersHandler.markStale(
        `Supplier ${event.supplierProjectId} status changed to ${event.newStatus}`,
        true // triggerImmediate
      );

      // Trigger immediate reload - no delay needed
      log.log('[WebSocket] Supplier status changed - triggering immediate reload');

      this.store.supplychainSuppliersHandler.loadSupplychainSuppliers(false).catch(err => {
        log.warn('[WebSocket] Error reloading suppliers after status change:', err);
      });
    }
  }

  /**
   * Check if user is currently on the Supply Chain Management page
   * @private
   * @returns {boolean}
   */
  _isOnSupplyChainPage() {
    if (typeof window !== 'undefined' && window.location) {
      return window.location.pathname.includes('supply-chain') ||
             window.location.pathname.includes('scm');
    }
    return false;
  }

  /**
   * Check if user is currently on the Business Segments / Customer Requests page
   * @private
   * @returns {boolean}
   */
  _isOnBusinessSegmentsPage() {
    if (typeof window !== 'undefined' && window.location) {
      return window.location.pathname.includes('self-assessment') ||
             window.location.pathname.includes('business-segment') ||
             window.location.pathname.includes('customer-request');
    }
    return false;
  }

  /**
   * Handle new invitation event (Supplier receives this)
   * Operation Lightsaber Part 2: Real-time notification when Lead Firm invites existing supplier
   * Backend already populated customer-requests from Snowflake, we just reload from Strapi
   *
   * @private
   * @param {Object} event - The new invitation event
   */
  handleNewInvitationEvent(event) {
    const currentProjectId = this.store.getProjectId();

    // Only process if this event is for our project (the Supplier)
    if (event.supplierProjectId &&
        parseInt(event.supplierProjectId) !== parseInt(currentProjectId)) {
      return;
    }

    log.log('[WebSocket] Received new invitation from Lead Firm:', {
        leadFirmProjectId: event.leadFirmProjectId,
        requesterCompanyName: event.requesterCompanyName,
        linkingId: event.linkingId,
        sector: event.requestedSector,
        country: event.requestedCountry
      });

    // Reload customer requests - backend already populated from Snowflake
    if (this.store.customerRequestsHandler) {
      this.store.customerRequestsHandler.loadCustomerRequests(true).catch(err => {
        log.warn('[WebSocket] Error refreshing customer requests after new invitation:', err);
      });
    }

    // Also reload business segments since a new invitation may mean new segment combo
    if (this.store.businessSegmentsHandler) {
      this.store.businessSegmentsHandler.loadBusinessSegments(true).catch(err => {
        log.warn('[WebSocket] Error refreshing business segments after new invitation:', err);
      });
    }

    // Show notification to user if they're on the business segments page
    if (this._isOnBusinessSegmentsPage() && event.requesterCompanyName) {
      // Use the store's notification system if available
      if (this.store.showNotification) {
        this.store.showNotification({
          type: 'info',
          message: `New invitation received from ${event.requesterCompanyName}`,
          duration: 5000
        });
      }
    }
  }

  /**
   * Handle reminder received event (Supplier receives this from Lead Firm)
   * Operation Lightsaber: Backend already updated customer-request in Strapi (lastRequest timestamp)
   * We just reload from Strapi to show the "data requested" badge
   *
   * @private
   * @param {Object} event - The reminder received event
   */
  handleReminderReceivedEvent(event) {
    const currentProjectId = this.store.getProjectId();

    // Only process if this event is for our project (the Supplier)
    if (event.supplierProjectId &&
        parseInt(event.supplierProjectId) !== parseInt(currentProjectId)) {
      return;
    }

    log.log('[WebSocket] Received reminder from Lead Firm:', {
        leadFirmProjectId: event.leadFirmProjectId,
        linkingId: event.linkingId,
        linkingIds: event.linkingIds,
        action: event.action
      });

    // Debounce reload — same pattern as handleReminderEvent.
    // Multiple reminders from the same Lead Firm (remindAll) arrive in quick succession.
    if (this._reminderDebounceTimer) {
      clearTimeout(this._reminderDebounceTimer);
    }

    this._reminderDebounceTimer = setTimeout(() => {
      this._reminderDebounceTimer = null;

      if (this.store.customerRequestsHandler) {
        log.log('[WebSocket] Debounced reload after reminder received');
        this.store.customerRequestsHandler.loadCustomerRequests(true).catch(err => {
          log.warn('[WebSocket] Error refreshing customer requests after reminder received:', err);
        });
      }
    }, 500);
  }

  /**
   * Handle reminder events
   * Operation Lightsaber: Backend already updated Strapi, frontend did optimistic update
   * No need to reload - just refresh customer requests for supplier-side UI
   *
   * DEBOUNCE: When multiple reminders fire (e.g. remindAll sends one event per supplier),
   * we coalesce into a single reload after 500ms of quiet. Without this, each event
   * triggers a full loadCustomerRequests → multiple reloads race → badges flash/reactivate.
   *
   * @private
   * @param {Object} event - The reminder event
   */
  handleReminderEvent(event) {
    log.log('Processing reminder event:', event.type);

    // Clear any pending debounce — we'll wait for the batch to finish
    if (this._reminderDebounceTimer) {
      clearTimeout(this._reminderDebounceTimer);
    }

    // Debounce: coalesce rapid-fire reminder events into one reload
    this._reminderDebounceTimer = setTimeout(() => {
      this._reminderDebounceTimer = null;

      if (this.store.customerRequestsHandler) {
        log.log('[Reminder] Debounced reload firing');
        this.store.customerRequestsHandler.loadCustomerRequests(true).catch(error => {
          log.error('Error refreshing customer requests after reminder:', error);
        });
      }
    }, 500);

    // ✅ Operation Lightsaber: No supplier reload needed
    // Frontend already did optimistic update when API returned
    // Backend already synced to Strapi before sending WebSocket event
  }


  /**
   * Handle submission events
   * Updates both company questions and business segments with immutable references
   * 
   * OPTIMIZATION: Uses force=false because backend already updated local DB before
   * sending WebSocket event. No need to query Snowflake again.
   * 
   * @private
   * @param {Object} event - The submission event
   */
  handleSubmissionEvent(event) {
    log.log('Processing submission event - updating ALL assessment statuses');

    const submissionTimestamp = event.timestamp || new Date().toISOString();

    // STEP 1: Lock down the customer requests handler BEFORE any reloads can fire.
    // - globalLastSubmitDate: prevents getLastSubmissionDate from regressing to stale Snowflake data
    // - submissionCooldownUntil: blocks silent reloads from repopulate cascade for 30s
    // Without this, repopulateAfterAction triggers a reload that recalculates needsAttention
    // using stale dates (Snowflake hasn't propagated yet), causing badges to flash back.
    if (this.store.customerRequestsHandler) {
      this.store.customerRequestsHandler.globalLastSubmitDate = submissionTimestamp;
      this.store.customerRequestsHandler.submissionCooldownUntil = Date.now() + 30000;
    }

    // STEP 2: Update store IMMEDIATELY so badges/UI reflect submission right away.
    // This is the primary mechanism — don't wait for repopulate chains or submission:status.
    this.updateAllModulesToSubmitted(submissionTimestamp);

    // STEP 3: Also trigger backend repopulation to sync Strapi with Snowflake.
    // Uses force=false since backend already updated local DB before sending this event.
    if (this.store.dataPopulationHandler) {
      this.store.dataPopulationHandler.repopulateAfterAction(
        ['customerRequests', 'businessSegments', 'companyQuestions', 'supplychainSuppliers'],
        false, // force=false to use already-updated local DB
        { action: 'submit', timestamp: submissionTimestamp }
      ).catch(error => {
        log.error('Error in repopulation after submission:', error);
        // Store already updated in step 2, this is non-critical
      });
    }
  }

  /**
   * Handle populate events
   * Intelligently handles Carbon Accounting modules sequentially
   * 
   * @private
   * @param {Object} event - The populate event
   */
  handlePopulateEvent(event) {
    let moduleName = event.type.split(':')[0];

    const moduleMap = {
      'business-segment': 'businessSegments',
      'company-question': 'companyQuestions',
      'customer-request': 'customerRequests',
      'supplychain-supplier': 'supplychainSuppliers',
      'emission-result': 'emissionResults',
      'ca-activity-data': 'activityData',
      'ca-spend-volume': 'spendVolume',
      'ca-spend-distribution': 'spendDistribution',
      'ca-supplier-input': 'supplierInput'
    };

    moduleName = moduleMap[moduleName] || moduleName;

    // OPTIMIZATION: If this is a Carbon Accounting module, check if we need to
    // populate multiple modules in sequence
    const carbonAccountingModules = [
      'emissionResults', 'activityData', 'spendVolume', 
      'spendDistribution', 'supplierInput'
    ];

    if (carbonAccountingModules.includes(moduleName) && this.store.dataPopulationHandler) {
      log.log(`Population event for Carbon Accounting module ${moduleName}, handling sequentially`);
      
      // Use the optimized sequential loading for Carbon Accounting modules
      this.store.dataPopulationHandler.loadCarbonAccountingModulesSequentially(
        this.store.getProjectId()
      ).catch(error => {
        log.error('Error sequentially loading Carbon Accounting modules:', error);
      });
      
      return;
    }

    // For non-Carbon Accounting modules, use the standard approach
    const handlerMethodMap = {
      'businessSegments': 'businessSegmentsHandler.loadBusinessSegments',
      'companyQuestions': 'companyQuestionsHandler.loadCompanyQuestions',
      'customerRequests': 'customerRequestsHandler.loadCustomerRequests',
      'supplychainSuppliers': 'supplychainSuppliersHandler.loadSupplychainSuppliers',
      'emissionResults': 'emissionsHandler.loadAllEmissionResults',
      'activityData': 'activitiesHandler.loadActivityData',
      'spendVolume': 'spendHandler.loadSpendVolume',
      'spendDistribution': 'spendDistributionHandler.loadSpendDistribution',
      'supplierInput': 'supplierInputHandler.loadSupplierInput'
    };

    if (handlerMethodMap[moduleName]) {
      const [handlerName, methodName] = handlerMethodMap[moduleName].split('.');

      if (this.store[handlerName] && typeof this.store[handlerName][methodName] === 'function') {
        log.log(`Reloading ${moduleName} data after population event`);
        // Backend already populated — just GET fresh data.
        // For businessSegments, force=true triggers another POST populate → infinite loop.
        // For customerRequests, true=silent (desired). For others, true=forceRefresh (safe).
        const arg = moduleName === 'businessSegments' ? false : true;
        this.store[handlerName][methodName](arg).catch(error => {
          log.error(`Error reloading ${moduleName} after population:`, error);
        });
      }
    }
  }

  /**
   * Handle invite events
   * Operation Lightsaber: Backend already updated Strapi before sending WebSocket event
   * Just reload from Strapi - no Snowflake population needed
   *
   * @private
   * @param {Object} event - The invite event
   */
  handleInviteEvent(_event) {
    log.log('[WebSocket] Processing invite event');

    // Reload customer requests from Strapi
    if (this.store.customerRequestsHandler) {
      this.store.customerRequestsHandler.loadCustomerRequests(true).catch(err => {
        log.warn('[WebSocket] Error refreshing customer requests after invite:', err);
      });
    }

    // Mark suppliers as stale and reload from Strapi
    if (this.store.supplychainSuppliersHandler) {
      this.store.supplychainSuppliersHandler.markStale('Invite event received');
      this.store.supplychainSuppliersHandler.loadSupplychainSuppliers(false).catch(err => {
        log.warn('[WebSocket] Error refreshing suppliers after invite:', err);
      });
    }
  }

  /**
   * Handle status change events (accept/decline)
   * Operation Lightsaber: Only Accept needs force=true (new sector/country combos)
   * Decline and other status changes just reload from Strapi
   *
   * @private
   * @param {Object} event - The status change event
   */
  handleStatusChangeEvent(event) {
    log.log(`[WebSocket] Processing ${event.action} event`);

    const isAccept = event.action?.toLowerCase() === 'accept' && event.type.includes('customer-request');

    // Always reload customer requests from Strapi (NOT Snowflake).
    // The backend respond() already wrote the status change directly to Strapi.
    // A Snowflake repopulate here would be destructive — it deletes all records
    // and recreates from Snowflake, which may not have propagated the accept yet.
    if (this.store.customerRequestsHandler) {
      this.store.customerRequestsHandler.loadCustomerRequests(true).catch(err => {
        log.warn('[WebSocket] Error refreshing customer requests after status change:', err);
      });
    }

    // For accepts, also repopulate business segments from Snowflake
    // (new sector/country combos need to be created for the newly accepted request)
    if (isAccept && this.store.dataPopulationHandler) {
      this.store.dataPopulationHandler.repopulateAfterAction(
        ['businessSegments'],
        true,
        { action: 'accept', source: event.type }
      );
    }
  }

  /**
   * Update all assessment modules to submitted status
   * Uses proper immutable updates for React/Gatsby
   * 
   * @private
   * @param {string} submissionTimestamp - ISO timestamp of submission
   */
  updateAllModulesToSubmitted(submissionTimestamp) {
    // Update business segments status
    if (this.store.state.data.businessSegments?.bySector) {
      log.log('Updating business segments status to "submitted"');
      const businessSegments = this.store.state.data.businessSegments;
      
      // Create a new bySector object
      const updatedBySector = {};
      let segmentsUpdated = false;

      // For each sector, create a new array of segments
      Object.keys(businessSegments.bySector).forEach(sector => {
        updatedBySector[sector] = businessSegments.bySector[sector].map(segment => {
          if (segment.assessmentStatus !== 'submitted') {
            segmentsUpdated = true;
            // Create NEW object instead of mutating
            return {
              ...segment,
              assessmentStatus: 'submitted',
              submittedAt: submissionTimestamp
            };
          }
          return segment;
        });
      });

      if (segmentsUpdated) {
        // Update with a completely new object
        this.store.state.data.businessSegments = {
          ...businessSegments,
          bySector: updatedBySector,
          meta: {
            ...businessSegments.meta,
            lastUpdated: submissionTimestamp
          }
        };

        this.store.notifySubscribers('businessSegments', this.store.state.data.businessSegments);
      }
    }

    // Update company questions status
    if (this.store.state.data.companyQuestions?.data) {
      log.log('Updating company questions status to "submitted"');
      const companyQuestions = this.store.state.data.companyQuestions.data;

      if (companyQuestions.assessmentStatus !== 'submitted') {
        // Create NEW data object instead of mutating
        const updatedQuestions = {
          ...companyQuestions,
          assessmentStatus: 'submitted'
        };

        // Update with new object
        this.store.state.data.companyQuestions = {
          ...this.store.state.data.companyQuestions,
          data: updatedQuestions
        };

        this.store.notifySubscribers('companyQuestions', this.store.state.data.companyQuestions);
      }
    }

    // Update customer requests (mark all accepted requests as submitted)
    if (this.store.state.data.customerRequests?.requests) {
      log.log('Updating customer requests with submission date');
      const requests = this.store.state.data.customerRequests.requests;
      
      // Create new array of request objects
      const updatedRequests = requests.map(request => {
        if (request.Status?.toLowerCase() === 'accepted') {
          // Create NEW object instead of mutating
          return {
            ...request,
            LastSubmitDate: submissionTimestamp,
            needsAttention: false
          };
        }
        return request;
      });

      // Create new customerRequests object
      this.store.state.data.customerRequests = {
        ...this.store.state.data.customerRequests,
        requests: updatedRequests,
        lastSubmitDate: submissionTimestamp,
        openCount: updatedRequests.filter(r => r.needsAttention).length
      };

      this.store.notifySubscribers('customerRequests', this.store.state.data.customerRequests);
    }
  }

  /**
   * Refresh critical data when connection is established
   * Operation Lightsaber: Just reload from Strapi, no Snowflake population
   * Strapi data is already aligned from previous session actions
   *
   * @private
   */
  refreshDataOnConnection() {
    // Reload customer requests from Strapi only if data is stale (silent mode)
    // OPTIMIZATION: Skip if data was just loaded (e.g., during initialization)
    if (this.store.customerRequestsHandler) {
      const FRESHNESS_MS = 30 * 1000; // 30 seconds
      const lastSync = this.store.state.data.customerRequests?.meta?.lastSync;
      const isStale = !lastSync || (Date.now() - new Date(lastSync).getTime() > FRESHNESS_MS);

      if (isStale) {
        this.store.customerRequestsHandler.loadCustomerRequests(true).catch(err => {
          log.warn('[WebSocket] Error refreshing customer requests on connection:', err);
        });
      } else {
        log.log('[WebSocket] Skipped customerRequests refresh (data is fresh)');
      }
    }

    // Only reload suppliers if marked stale
    if (this.store.supplychainSuppliersHandler) {
      const isStale = this.store.supplychainSuppliersHandler.dataFreshness?.isStale;
      if (isStale) {
        this.store.supplychainSuppliersHandler.loadSupplychainSuppliers(false).catch(err => {
          log.warn('[WebSocket] Error refreshing suppliers on connection:', err);
        });
      }
    }
  }

  /**
   * Register default handlers
   * @private
   */
  registerDefaultHandlers() {
    // Debug logger - only logs in development
    this.registerHandler(
      'debugLogger',
      (event) => {
        log.debug('%c WebSocket Event', 'background: #333; color: #bada55', event);
      },
      ['*']
    );
  }
}